
/** The minimum an entity must expose to be clickable. */
export interface TargetCandidate {
  readonly id: number;
  readonly xMm: number;
  readonly yMm: number;
}

export interface Projection {
  project(xMm: number, yMm: number, zMm?: number): { x: number; y: number; behind: boolean };
}

export type GroundHeight = (xMm: number, yMm: number) => number;

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface Unprojection {
  unproject(px: number, py: number, groundZMm: number): { xMm: number; yMm: number } | null;
}

export const GROUND_PICK_TOLERANCE_MM = 100;

/** How many times to re-ask. Four is the measured ceiling: on a1's steepest
 *  imported slope the third pass is already inside the tolerance, and a loop
 *  without a bound is a loop a pathological height field can hang the frame
 *  with. */
export const GROUND_PICK_STEPS = 4;

export function pickGround(
  pointer: ScreenPoint,
  camera: Unprojection,
  groundAtMm: GroundHeight,
  seedZMm: number,
): { xMm: number; yMm: number } | null {
  let heightMm = seedZMm;
  let point = camera.unproject(pointer.x, pointer.y, heightMm);
  if (point === null) return null;

  for (let step = 0; step < GROUND_PICK_STEPS; step++) {
    const sampled = groundAtMm(point.xMm, point.yMm);
    if (Math.abs(sampled - heightMm) <= GROUND_PICK_TOLERANCE_MM) return point;
    heightMm = sampled;
    const next = camera.unproject(pointer.x, pointer.y, heightMm);
    // A height the ray cannot reach going forward is not a refinement of the
    // previous answer, it is the absence of one -- and returning the stale
    // point would put the player's destination on a plane the terrain is not on.
    if (next === null) return null;
    point = next;
  }
  return point;
}

export const TARGET_PICK_RADIUS_PX = 52;
/** Slightly larger than a character at the default zoom. Tuned for a thumb, not
 *  for a mouse: a mouse user loses nothing by the extra slack, and a phone player
 *  loses the game without it. */

export function pickTarget(
  candidates: readonly TargetCandidate[],
  pointer: ScreenPoint,
  projection: Projection,
  groundAtMm: GroundHeight,
  radiusPx: number = TARGET_PICK_RADIUS_PX,
): number | null {
  const limit = radiusPx * radiusPx;
  let bestId: number | null = null;
  let bestDistance = Infinity;

  for (const candidate of candidates) {
    const projected = projection.project(
      candidate.xMm,
      candidate.yMm,
      groundAtMm(candidate.xMm, candidate.yMm),
    );
    // Behind the camera has no honest screen distance -- see `View.project` --
    // so a candidate there is unpickable rather than compared against a mirrored
    // position.
    if (projected.behind) continue;
    const dx = projected.x - pointer.x;
    const dy = projected.y - pointer.y;
    const distance = dx * dx + dy * dy;
    if (distance > limit) continue;
    if (distance < bestDistance || (distance === bestDistance && bestId !== null && candidate.id < bestId)) {
      bestDistance = distance;
      bestId = candidate.id;
    }
  }
  return bestId;
}

/** One body the pointer's line may cross: where it stands, whether it is dead,
 *  and its defending spheres as this frame placed them. */
export interface SpherePickCandidate {
  readonly id: number;
  readonly dead: boolean;
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly spheres: readonly { readonly xMm: number; readonly yMm: number; readonly zMm: number; readonly radiusMm: number }[];
}

/** The pointer's line through the world: a point on it and its direction. */
export interface PointerRay {
  readonly originMm: readonly [number, number, number];
  readonly dirMm: readonly [number, number, number];
}

/** `__UpdatePickedActorList`, `__SortPickedActorList` and `__NEW_Pick`'s ordered
 *  pass (`PythonCharacterManager.cpp:684-704,706-735,776-797`): every body whose
 *  defending sphere the pointer's line crosses (`IntersectDefendingSphere`,
 *  `ActorInstance.cpp:753-782`), the living before the dead and then the nearer
 *  the camera's eye, and the first of them; null when the line crosses none. */
export function pickByDefendingSphere(
  candidates: readonly SpherePickCandidate[],
  ray: PointerRay,
  eyeMm: readonly [number, number, number],
): number | null {
  const [ox, oy, oz] = ray.originMm;
  const length = Math.hypot(ray.dirMm[0], ray.dirMm[1], ray.dirMm[2]) || 1;
  const [ux, uy, uz] = [ray.dirMm[0] / length, ray.dirMm[1] / length, ray.dirMm[2] / length];
  let best: SpherePickCandidate | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const crossed = candidate.spheres.some((sphere) => {
      const dx = ox - sphere.xMm; // ActorInstance.cpp:773
      const dy = oy - sphere.yMm;
      const dz = oz - sphere.zMm;
      const b = ux * dx + uy * dy + uz * dz; // :774
      const c = dx * dx + dy * dy + dz * dz - sphere.radiusMm * sphere.radiusMm; // :775
      return b * b - c >= 0; // :777
    });
    if (!crossed) continue;
    const ex = candidate.xMm - eyeMm[0];
    const ey = candidate.yMm - eyeMm[1];
    const ez = candidate.zMm - eyeMm[2];
    const distance = ex * ex + ey * ey + ez * ez; // CalculateDistanceSq3d, PythonCharacterManager.cpp:720
    if (best === null
      || (best.dead && !candidate.dead) // :715-719
      || (best.dead === candidate.dead && distance < bestDistance)) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best === null ? null : best.id;
}

export const AUTO_TARGET_RANGE_MM = 3_000;

export function nearestAttackable(
  candidates: readonly TargetCandidate[],
  fromXMm: number,
  fromYMm: number,
  rangeMm: number = AUTO_TARGET_RANGE_MM,
): number | null {
  const limit = rangeMm * rangeMm;
  let bestId: number | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const dx = candidate.xMm - fromXMm;
    const dy = candidate.yMm - fromYMm;
    const distance = dx * dx + dy * dy;
    if (distance > limit) continue;
    if (distance < bestDistance || (distance === bestDistance && bestId !== null && candidate.id < bestId)) {
      bestDistance = distance;
      bestId = candidate.id;
    }
  }
  return bestId;
}

/** What a swing needs to know about a candidate on top of its position: its
 *  kind, and its health only so far as it says whether the thing is already
 *  dead. See `isAttackable`. */
export interface AttackableCandidate extends TargetCandidate {
  readonly kind: number;
  readonly hp: number;
  readonly maxHp: number;
}

/** `EntityKind.PLAYER`, `MONSTER`, `STONE`, duplicated from the connection to
 *  keep this module dependency-free, exactly as `loot.ts` duplicates
 *  `ITEM_KIND`. */
const PLAYER_KIND = 0;
const MONSTER_KIND = 1;
const STONE_KIND = 3;

export function isAttackable(entity: {
  readonly kind: number;
  readonly hp: number;
  readonly maxHp: number;
}): boolean {
  if (
    entity.kind !== PLAYER_KIND &&
    entity.kind !== MONSTER_KIND &&
    entity.kind !== STONE_KIND
  ) {
    return false;
  }
  return !(entity.maxHp > 0 && entity.hp <= 0);
}

export function pickVictim<T extends AttackableCandidate>(
  selected: T | null,
  candidates: readonly T[],
  fromXMm: number,
  fromYMm: number,
  rangeMm: number = AUTO_TARGET_RANGE_MM,
): T | null {
  const limit = rangeMm * rangeMm;
  const inReach = (entity: T): boolean => {
    const dx = entity.xMm - fromXMm;
    const dy = entity.yMm - fromYMm;
    return dx * dx + dy * dy <= limit;
  };
  if (selected !== null && isAttackable(selected) && inReach(selected)) return selected;
  // No separate loot test: `ITEM` is not one of the three kinds `isAttackable`
  // admits, so a dropped sword is refused by the same one rule everything else
  // is -- one lookup per fact.
  const attackable = candidates.filter(isAttackable);
  const foundId = nearestAttackable(attackable, fromXMm, fromYMm, rangeMm);
  return foundId === null ? null : (attackable.find((entity) => entity.id === foundId) ?? null);
}

export class TargetTracker {
  private current: number | null = null;
  private flyingUntilMs = 0;

  constructor(private readonly clock: () => number = () => performance.now()) {}

  get id(): number | null {
    return this.current;
  }

  /** The main actor's playing motion has a fly event until `untilMs`, and
   *  `CActorInstance::CanChangeTarget` answers false until then
   *  (`ActorInstanceFly.cpp:111-116`, `ActorInstanceMotion.cpp:828-848`):
   *  `select` and `clear` are refused, as `CPythonPlayer::SetTarget` returns
   *  before touching the target (`PythonPlayerInput.cpp:119`). */
  holdWhileFlying(untilMs: number): void {
    this.flyingUntilMs = untilMs;
  }

  select(id: number | null): void {
    if (this.clock() < this.flyingUntilMs) return; // PythonPlayerInput.cpp:119
    // Zero is the protocol's "no target" sentinel in `Attack`, so it can never be
    // a real selection; treating it as one would send an abort that reads as a
    // swing at entity zero.
    this.current = id === 0 ? null : id;
  }

  clear(): void {
    if (this.clock() < this.flyingUntilMs) return; // PythonPlayerInput.cpp:119
    this.current = null;
  }

  /** Select a body another rule already picked. */
  choose(id: number): number | null {
    this.select(id);
    return this.current;
  }

  /** Pick from a tap. Empty ground clears, which is how a player deselects. */
  pick(
    candidates: readonly TargetCandidate[],
    pointer: ScreenPoint,
    projection: Projection,
    groundAtMm: GroundHeight,
    radiusPx: number = TARGET_PICK_RADIUS_PX,
  ): number | null {
    this.select(pickTarget(candidates, pointer, projection, groundAtMm, radiusPx));
    return this.current;
  }

  /** Forget a target the world no longer contains. */
  prune(alive: ReadonlySet<number>): void {
    if (this.current !== null && !alive.has(this.current)) this.current = null;
  }
}
